import type { PracticeSummary } from "../../practice/types.ts";
import { lazy, Suspense, useState } from "react";
import { whiteMoveNumber } from "../../trainer/phases.ts";
import { onBook } from "../../trainer/openingPath.ts";
import {
  SCORECARD_SLOTS,
  describeEngineChoices,
  medalLiveText,
  medalStreak,
  ordinal,
  rankLabel,
  rankTier,
  summarizeRanks,
  type MedalName,
} from "../../trainer/medals.ts";
import {
  reviewResultSentence,
  type HistoryEntry,
  type ReviewResult,
  type TrainMode,
} from "../../trainer/session.ts";

const EnginePerspective = lazy(() => import("./EnginePerspective.tsx"));

export function MedalIcon({ medal }: { medal: MedalName }) {
  const palette = {
    gold: {
      ribbon: "#c99d4a",
      rim: "#8a6420",
      face: "#e8c66a",
      inner: "#f4dd92",
      ink: "#6d4c0f",
      mark: "1",
    },
    silver: {
      ribbon: "#9aa4ad",
      rim: "#5a6a73",
      face: "#d4dadf",
      inner: "#e9eef2",
      ink: "#3c4a52",
      mark: "2",
    },
    bronze: {
      ribbon: "#a36b3f",
      rim: "#6a3e15",
      face: "#c78350",
      inner: "#dca078",
      ink: "#4f2b0b",
      mark: "3",
    },
  }[medal];
  return (
    <svg className="medal-icon" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <path d="M10 2 L16 18 L24 18 L30 2 Z" fill={palette.ribbon} opacity="0.85" />
      <circle cx="20" cy="26" r="11" fill={palette.face} stroke={palette.rim} strokeWidth="1.4" />
      <circle cx="20" cy="26" r="7" fill={palette.inner} />
      <text
        x="20"
        y="30"
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill={palette.ink}
        fontFamily="Georgia, serif"
      >
        {palette.mark}
      </text>
    </svg>
  );
}

/** The open-book mark the trail starts from: here, "this was a book move". */
export function BookBadge() {
  return (
    <svg className="book-badge" viewBox="0 0 16 12" aria-hidden="true" focusable="false">
      <path d="M1.5 2.2q3-1.2 6.5.6v8.2q-3.5-1.8-6.5-.6Zm13 0q-3-1.2-6.5.6v8.2q3.5-1.8 6.5-.6Z" />
    </svg>
  );
}

/** A book move ranked below the medals: the rank stands, the verdict is the book's. */
function bookMoveUnmedalled(entry: HistoryEntry): boolean {
  return (
    onBook(entry.openingPath) &&
    entry.medalStatus === "verified" &&
    !entry.medal &&
    !entry.withheld &&
    (entry.rank == null || entry.rank > 3)
  );
}

/** What Stockfish says about a move, said so that a book move is never "wrong" for it. */
export function stockfishSentence(entry: HistoryEntry): string {
  const text = medalLiveText(entry);
  return bookMoveUnmedalled(entry)
    ? `${text.replace(/ No medal\.$/, "")} A book move needs no medal: the course teaches it for its plan.`
    : text;
}

/** The first three choices and their gaps, from the same search as the rank. */
export function stockfishChoices(entry: HistoryEntry): string {
  return describeEngineChoices(
    entry.san,
    entry.engineChoices,
    entry.assessment?.tactical.cpLoss ?? null
  );
}

function cellLabel(n: number, entry: HistoryEntry | undefined): string {
  if (!entry) return `Move ${n}, not played yet`;
  const choices = stockfishChoices(entry);
  return `Move ${n}.${onBook(entry.openingPath) ? " Book move." : ""} Stockfish: ${stockfishSentence(entry)}${choices ? ` ${choices}` : ""}`;
}

function ScoreCell({ n, entry, current }: { n: number; entry?: HistoryEntry; current: boolean }) {
  const medal = entry?.medal;
  const tier = entry?.medalStatus === "verified" ? rankTier(entry.percentile ?? null) : null;
  const classes = ["scorecard-cell"];
  if (!entry) classes.push("empty");
  else if (entry.medalStatus === "pending") classes.push("filled", "pending");
  else if (medal) classes.push("filled", "medal", `medal-${medal}`, `medal-arrive-${medal}`);
  else classes.push("filled");
  const book = Boolean(entry && onBook(entry.openingPath));
  // A book move keeps its rank and bar, but never the "weak" warning colours:
  // a low rank there is Stockfish's preference, not a mistake.
  if (tier && !(book && (tier === "middling" || tier === "weak"))) classes.push(`tier-${tier}`);
  if (book) classes.push("on-book");
  if (entry?.withheld) classes.push("withheld");

  const label = rankLabel(entry);
  const choices = entry ? stockfishChoices(entry) : "";
  let big = "";
  let small = "";
  if (entry?.medalStatus === "pending") big = "…";
  else if (entry && entry.medalStatus !== "verified") big = "–";
  else if (entry && !medal && entry.rank != null) {
    big = ordinal(entry.rank);
    small = entry.total ? `/ ${entry.total}` : "";
  } else if (entry && !medal && entry.outsideTop && entry.rankedCount) {
    big = `${entry.rankedCount}+`;
    small = entry.total ? `/ ${entry.total}` : "";
  }
  const fill =
    entry?.medalStatus === "verified" && entry.percentile != null
      ? Math.max(4, 100 - entry.percentile)
      : null;

  return (
    <li
      className={classes.join(" ")}
      aria-current={current ? "true" : undefined}
      aria-label={cellLabel(n, entry)}
      title={
        entry?.san
          ? `${n}${entry.ply % 2 ? "." : "…"} ${entry.san}${label ? ` · ${label}` : ""}${choices ? `. ${choices}` : ""}`
          : undefined
      }
    >
      <span className="cell-index">{n}</span>
      {book ? (
        <span className="cell-book" title="Book move">
          <BookBadge />
        </span>
      ) : null}
      {medal ? (
        <>
          <MedalIcon medal={medal} />
          {label ? <span className="cell-rank-small">{label}</span> : null}
        </>
      ) : (
        <>
          {big ? <span className="cell-rank">{big}</span> : null}
          {small ? <span className="cell-of">{small}</span> : null}
          {entry?.earnedMedal && entry.withheld ? (
            <span className="cell-withheld">{entry.earnedMedal} withheld</span>
          ) : null}
        </>
      )}
      {fill != null ? (
        <span className="cell-bar" aria-hidden="true">
          <span style={{ width: `${fill}%` }} />
        </span>
      ) : null}
    </li>
  );
}

function SessionCell({
  averagePercentile,
  ranked,
}: {
  averagePercentile: number | null;
  ranked: number;
}) {
  const label =
    averagePercentile == null
      ? "Session average rank, not available yet"
      : `Session average: top ${averagePercentile}% across ${ranked} ranked moves`;
  return (
    <li
      className={`scorecard-cell session${averagePercentile == null ? " empty" : " filled"}`}
      aria-label={label}
      title={label}
    >
      <span className="cell-index">Avg</span>
      {averagePercentile != null ? (
        <>
          <span className="cell-rank">Top {averagePercentile}%</span>
          <span className="cell-of">{ranked} ranked</span>
        </>
      ) : null}
    </li>
  );
}

export type ReviewProgress = { total: number; remaining: number; result: ReviewResult | null };

export function Scorecard({
  history,
  mode,
  review,
  memory,
  practice,
}: {
  history: HistoryEntry[];
  mode: TrainMode;
  review?: ReviewProgress;
  memory?: { found: number; asked: number };
  practice?: PracticeSummary;
}) {
  const [explain, setExplain] = useState(false);
  const students = history.filter((entry) => entry.byStudent && !entry.setup);
  const streak = medalStreak(students);
  const current = students[students.length - 1];
  const summary = summarizeRanks(students);

  if (mode === "review") {
    const total = review?.total || 0;
    const position = total ? Math.min(total, total - (review?.remaining || 0) + 1) : 0;
    const result = review?.result || null;
    return (
      <section className="scorecard review-status" id="scorecard" aria-label="Repeat positions">
        <div className="scorecard-head">
          <h3 className="scorecard-title">Repeat positions</h3>
          {position ? (
            <span className="review-count">
              Position {position} of {total}
            </span>
          ) : null}
        </div>
        {result ? (
          <>
            <p className={`review-result ${result.pass ? "is-pass" : "is-miss"}`}>
              <strong>Last position.</strong> {reviewResultSentence(result)}
            </p>
            {result.lesson ? <p className="review-lesson">{result.lesson}</p> : null}
          </>
        ) : null}
        <p className="scorecard-callout">
          Play the move the course teaches here. A miss comes back before this sitting ends; miss it
          again and it returns to the first step. Moves here earn no medals.
        </p>
      </section>
    );
  }

  const played = students.length ? whiteMoveNumber(students[students.length - 1].ply) : 0;
  // Free play keeps the card growing in rows of five past the course.
  const slots = Math.max(SCORECARD_SLOTS, Math.ceil(played / 5) * 5);
  const bySlot = new Map<number, HistoryEntry>();
  for (const entry of students) {
    const slot = whiteMoveNumber(entry.ply);
    if (slot >= 1 && slot <= slots) bySlot.set(slot, entry);
  }
  const describe = (item: { index: number; entry: { san?: string } } | null) =>
    item
      ? `${whiteMoveNumber(students[item.index].ply)}${students[item.index].ply % 2 ? "." : "…"} ${item.entry.san} · ${rankLabel(item.entry)}${onBook(students[item.index].openingPath) ? " · book move" : ""}`
      : "";

  return (
    <section
      className="scorecard"
      id="scorecard"
      aria-label={
        slots > SCORECARD_SLOTS
          ? `Move rankings for your first ${slots} moves`
          : "Move rankings for your first 15 moves"
      }
    >
      <header className="scorecard-perspective-head">
        <h3 className="scorecard-title">
          Stockfish’s view <span className="scorecard-perspective-tag">a second opinion</span>
        </h3>
        <p>
          The course teaches book moves; Stockfish ranks every legal move by its own search. A book
          move <BookBadge /> without a medal is still the right lesson.
        </p>
        {mode === "recall" ? (
          <p className="scorecard-callout">
            {practice
              ? `${practice.independent} found alone · ${practice.cued} with a cue · ${practice.repaired} repaired · ${practice.missed} to revisit`
              : `Found ${memory?.found ?? 0} of ${memory?.asked ?? 0} with the move still unnamed.`}
          </p>
        ) : null}
      </header>
      <details
        className="scorecard-perspective"
        onToggle={(event) => setExplain(event.currentTarget.open)}
      >
        <summary>Why the two views can disagree</summary>
        {explain ? (
          <Suspense fallback={<p>Loading the explanation…</p>}>
            <EnginePerspective />
          </Suspense>
        ) : null}
      </details>
      {streak >= 3 ? (
        <div className="scorecard-head">
          <span className="scorecard-streak">{streak} medals in a row</span>
        </div>
      ) : null}
      {summary.ranked > 0 ? (
        <ul className="rank-summary" aria-label="Session ranking summary">
          <li>
            <span className="rank-summary-label">Average</span>
            <strong>Top {summary.averagePercentile}%</strong>
          </li>
          <li>
            <span className="rank-summary-label">Medals</span>
            <strong className="rank-summary-medals">
              <span>
                <MedalIcon medal="gold" /> {summary.gold}
              </span>
              <span>
                <MedalIcon medal="silver" /> {summary.silver}
              </span>
              <span>
                <MedalIcon medal="bronze" /> {summary.bronze}
              </span>
            </strong>
          </li>
          {summary.best ? (
            <li>
              <span className="rank-summary-label">Highest rank</span>
              <strong>{describe(summary.best)}</strong>
            </li>
          ) : null}
          {summary.worst ? (
            <li>
              <span className="rank-summary-label">Lowest rank</span>
              <strong>{describe(summary.worst)}</strong>
            </li>
          ) : null}
        </ul>
      ) : null}
      <ol className="scorecard-grid" style={{ gridTemplateRows: "auto" }}>
        {Array.from({ length: slots }, (_, index) => {
          const n = index + 1;
          const entry = bySlot.get(n);
          return (
            <ScoreCell
              key={`${n}-${entry?.medalStatus || "empty"}-${entry?.medal || ""}-${entry?.rank ?? ""}`}
              n={n}
              entry={entry}
              current={Boolean(
                entry && current && entry.ply === current.ply && entry.uci === current.uci
              )}
            />
          );
        })}
        <SessionCell averagePercentile={summary.averagePercentile} ranked={summary.ranked} />
      </ol>
      {current ? (
        <div className={`scorecard-callout${bookMoveUnmedalled(current) ? " is-book-move" : ""}`}>
          <p>Stockfish: {stockfishSentence(current)}</p>
          {stockfishChoices(current) ? <p>{stockfishChoices(current)}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
